import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable } from '@dnd-kit/core';
import { type CurveReference, getCurveId } from '../../../interfaces/waveChart';

/**
 * Clean, unified props for CurveChip
 * Works everywhere - no mode-specific complexity
 */
export interface CurveChipProps {
  /** The curve reference (fileType + curveName) */
  curveReference: CurveReference;
  
  /** Optional display name override (defaults to curveName) */
  displayName?: string;
  
  /** Optional unit display (e.g., "API", "psi") */
  unit?: string;
  
  /** Optional color override (for charts showing curve colors) */
  color?: string;
  
  /** Whether the chip is currently being dragged */
  isDragged?: boolean;
  
  /** Whether drag functionality is disabled */
  disabled?: boolean;
  
  /** Whether the component is clickable */
  isClickable?: boolean;
  
  /** Whether to show the remove (X) button */
  showRemoveButton?: boolean;
  
  /** Called when curve is clicked */
  onClick?: (curveRef: CurveReference) => void;
  
  /** Called when curve removal is requested */
  onRemove?: (curveRef: CurveReference) => void;
  
  /** Called when drag starts */
  onDragStart?: (curveRef: CurveReference) => void;
  
  /** Called when drag ends */
  onDragEnd?: () => void;
}

/**
 * Simple draggable chip component for curves
 * Clean, minimal, works everywhere
 */
export const CurveChip = React.memo<CurveChipProps>(({
  curveReference,
  displayName,
  unit,
  color,
  isDragged = false,
  disabled = false,
  isClickable = true,
  showRemoveButton = false,
  onClick,
  onRemove,
  onDragStart,
  onDragEnd
}) => {
  // Simple display name
  const label = displayName || curveReference.curveName;
  const fullLabel = unit ? `${label} (${unit})` : label;
  
  // dnd-kit setup
  const dragId = getCurveId(curveReference);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    disabled,
    data: { type: 'curve-from-explorer', curveReference }
  });

  // Handle drag state changes
  React.useEffect(() => {
    if (isDragging && onDragStart) {
      onDragStart(curveReference);
    } else if (!isDragging && onDragEnd) {
      onDragEnd();
    }
  }, [isDragging, onDragStart, onDragEnd, curveReference]);

  const handleClick = () => {
    if (!disabled && !isDragging && isClickable && onClick) {
      onClick(curveReference);
    }
  };

  const handleDelete = () => {
    if (!disabled && onRemove) {
      onRemove(curveReference);
    }
  };

  const tooltipText = `${fullLabel}${!disabled ? ' • Drag to move' : ''}`;
  
  return (
    <Tooltip title={tooltipText}>
      <Box
        ref={setNodeRef}
        sx={{
          touchAction: 'none', // ✅ Prevent browser touch gestures
          WebkitUserDrag: 'none', // ✅ SAFARI FIX: Disable native drag that conflicts with dnd-kit
          ...(transform ? {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          } : undefined)
        }}
      >
        <Chip
          {...(disabled || process.env.NODE_ENV === 'test' ? {} : listeners)}
          {...(disabled || process.env.NODE_ENV === 'test' ? {} : attributes)}
          icon={!disabled ? <DragIndicatorIcon /> : undefined}
          label={fullLabel}
          size="small"
          onClick={handleClick}
          onDelete={showRemoveButton && onRemove ? handleDelete : undefined}
          draggable={false}
          sx={{
            opacity: isDragging || isDragged ? 0.5 : 1,
            cursor: disabled ? 'default' : (isDragging || isDragged ? 'grabbing' : 'grab'),
            backgroundColor: color ? `${color}20` : undefined,
            borderColor: color,
            '&:hover': {
              backgroundColor: color ? `${color}30` : undefined,
            }
          }}
        />
      </Box>
    </Tooltip>
  );
});

CurveChip.displayName = 'CurveChip'; 